import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getPublicProfile } from '../store/user/thunks';
import PublicProfileSkeleton from './PublicProfileSkeleton';
import {
  Flex,
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Stack,
  Card,
  CardBody,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorMode,
  VStack,
} from '@chakra-ui/react';

export default function PublicProfile() {
  const { username } = useParams();
  const dispatch = useDispatch();
  const { publicProfile, publicProfileLoading, publicProfileError } = useSelector((state) => state.user);
  const { colorMode } = useColorMode();

  useEffect(() => {
    if (username) {
      dispatch(getPublicProfile({ username }));
    }
  }, [username, dispatch]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    const num = parseFloat(value);
    const formatted = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return num < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0%';
    return `${parseFloat(value).toFixed(1)}%`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (publicProfileLoading) {
    return (
      <Flex
        flexDirection="column"
        width="100wh"
        minHeight="100vh"
        backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}
        alignItems="center"
        pt={8}
        pb={8}
      >
        <Stack spacing={6} maxWidth="800px" width="90%" px={4}>
          <PublicProfileSkeleton />
        </Stack>
      </Flex>
    );
  }

  if (publicProfileError || (!publicProfileLoading && publicProfile && publicProfile.result && !publicProfile.username)) {
    return (
      <Flex
        flexDirection="column"
        width="100wh"
        minHeight="100vh"
        backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}
        justifyContent="center"
        alignItems="center"
      >
        <Box
          p={8}
          maxWidth="500px"
          borderRadius="lg"
          backgroundColor={colorMode === 'light' ? "white" : "gray.700"}
          boxShadow="md"
          textAlign="center"
        >
          <Heading size="md" mb={4}>Profile Not Available</Heading>
          <Text color="gray.500">
            {publicProfile?.result || "This profile does not exist or is set to private."}
          </Text>
        </Box>
      </Flex>
    );
  }

  if (!publicProfile || !publicProfile.username) {
    return null;
  }

  const { display_name, member_since, stats, pnl_calendar } = publicProfile;

  return (
    <Flex
      flexDirection="column"
      width="100wh"
      minHeight="100vh"
      backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}
      alignItems="center"
      pt={8}
      pb={8}
    >
      <Stack spacing={6} maxWidth="800px" width="90%" px={4}>
        {/* Profile Header */}
        <Card bg={colorMode === 'light' ? 'white' : 'gray.700'} boxShadow="sm" rounded="lg">
          <CardBody>
            <VStack spacing={2}>
              <Heading size="lg">{display_name || username}</Heading>
              <Text color="gray.500" fontSize="sm">@{publicProfile.username}</Text>
              {member_since && (
                <Badge colorScheme="blue" variant="subtle">
                  Member since {formatDate(member_since)}
                </Badge>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Stats Grid */}
        {stats && (
          <Card bg={colorMode === 'light' ? 'white' : 'gray.700'} boxShadow="sm" rounded="lg">
            <CardBody>
              <Heading size="sm" mb={4} textTransform="uppercase">Trading Stats</Heading>
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                <Stat>
                  <StatLabel>Total P&L</StatLabel>
                  <StatNumber
                    fontSize="lg"
                    color={stats.total_pnl >= 0 ? 'green.500' : 'red.500'}
                  >
                    {formatCurrency(stats.total_pnl)}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Win Rate</StatLabel>
                  <StatNumber fontSize="lg">
                    {formatPercent(stats.win_rate)}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Total Trades</StatLabel>
                  <StatNumber fontSize="lg">
                    {stats.num_trades || 0}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Wins</StatLabel>
                  <StatNumber fontSize="lg" color="green.500">
                    {stats.num_wins || 0}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Losses</StatLabel>
                  <StatNumber fontSize="lg" color="red.500">
                    {stats.num_losses || 0}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Largest Win</StatLabel>
                  <StatNumber fontSize="lg" color="green.500">
                    {formatCurrency(stats.largest_win)}
                  </StatNumber>
                </Stat>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {/* PnL Calendar */}
        {pnl_calendar && pnl_calendar.length > 0 && (
          <Card bg={colorMode === 'light' ? 'white' : 'gray.700'} boxShadow="sm" rounded="lg">
            <CardBody>
              <Heading size="sm" mb={4} textTransform="uppercase">Recent P&L</Heading>
              <TableContainer maxHeight="300px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Date</Th>
                      <Th isNumeric>P&L</Th>
                      <Th isNumeric>Trades</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pnl_calendar.slice(0, 30).map((entry, idx) => (
                      <Tr key={idx}>
                        <Td>{entry.trade_date}</Td>
                        <Td
                          isNumeric
                          color={parseFloat(entry.day_pnl || 0) >= 0 ? 'green.500' : 'red.500'}
                          fontWeight="semibold"
                        >
                          {formatCurrency(entry.day_pnl)}
                        </Td>
                        <Td isNumeric>{entry.day_count || '-'}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        )}


      </Stack>
    </Flex>
  );
}
